import React, {useContext, useState} from "react";
import {submitContactMessage} from "../../services/api";
import StyleContext from "../../contexts/StyleContext";
import GlobalLoader from "../../components/loader/GlobalLoader";
import "../../styles/_form.scss";
import "./ContactForm.scss";

const emptyForm = {name: "", email: "", subject: "", message: ""};

const ContactForm = () => {
  const {isDark} = useContext(StyleContext);
  const [form, setForm]       = useState(emptyForm);
  const [status, setStatus]   = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setError]  = useState("");
  const [emailSent, setEmailSent] = useState(true);

  const onChange = e => {
    const {name, value} = e.target;
    setForm(f => ({...f, [name]: value}));
  };

  const validate = () => {
    if (!form.name.trim())    return "Please enter your name";
    if (!form.email.trim())   return "Please enter your email";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "That email doesn't look right";
    if (!form.subject.trim()) return "Subject is required";
    if (!form.message.trim()) return "Message can't be empty";
    return null;
  };

  const onSubmit = async e => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); setStatus("error"); return; }

    setStatus("submitting");
    setError("");
    try {
      const res = await submitContactMessage({
        name:    form.name.trim(),
        email:   form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setEmailSent(!!res?.email_sent);
      setStatus("success");
      setForm(emptyForm);
    } catch (err) {
      setError(err?.message || "Something went wrong sending your message");
      setStatus("error");
    }
  };

  const formClass = `rd-form rd-contact-form${isDark ? " rd-form--dark" : ""}`;

  if (status === "success") {
    return (
      <div className={`${formClass} rd-contact-form--success`}>
        <div className="rd-contact-form__check" aria-hidden="true">✓</div>
        <h3>Message received</h3>
        <p>
          Thanks for reaching out — I'll get back to you as soon as I can.
          {!emailSent && (
            <>
              <br />
              <small className="rd-contact-form__hint">
                (Your message is safely saved. Email notification will retry automatically.)
              </small>
            </>
          )}
        </p>
        <button
          type="button"
          className="rd-contact-form__again"
          onClick={() => setStatus("idle")}
        >
          Send another
        </button>
      </div>
    );
  }

  const submitting = status === "submitting";

  return (
    <form className={formClass} onSubmit={onSubmit} noValidate>
      <div className="rd-form-group">
        <label className="rd-form-group__label" htmlFor="cf-name">
          Name <span className="rd-form-group__required">*</span>
        </label>
        <input
          id="cf-name"
          name="name"
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={onChange}
          disabled={submitting}
          maxLength={120}
          placeholder="Your full name"
          required
        />
      </div>

      <div className="rd-form-group">
        <label className="rd-form-group__label" htmlFor="cf-email">
          Email <span className="rd-form-group__required">*</span>
        </label>
        <input
          id="cf-email"
          name="email"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={onChange}
          disabled={submitting}
          maxLength={190}
          placeholder="you@example.com"
          required
        />
      </div>

      <div className="rd-form-group">
        <label className="rd-form-group__label" htmlFor="cf-subject">
          Subject <span className="rd-form-group__required">*</span>
        </label>
        <input
          id="cf-subject"
          name="subject"
          type="text"
          value={form.subject}
          onChange={onChange}
          disabled={submitting}
          maxLength={200}
          placeholder="What's this about?"
          required
        />
      </div>

      <div className="rd-form-group">
        <label className="rd-form-group__label" htmlFor="cf-message">
          Message <span className="rd-form-group__required">*</span>
        </label>
        <textarea
          id="cf-message"
          name="message"
          rows={6}
          value={form.message}
          onChange={onChange}
          disabled={submitting}
          maxLength={5000}
          placeholder="Tell me a bit about the project or idea…"
          required
        />
      </div>

      {status === "error" && errorMsg && (
        <p className="rd-form__error" role="alert">{errorMsg}</p>
      )}

      <button
        type="submit"
        className="rd-form__submit"
        disabled={submitting}
      >
        {submitting ? (
          <span className="rd-contact-form__busy">
            <GlobalLoader inline compact />
            <span className="rd-contact-form__busy-text">Sending…</span>
          </span>
        ) : "Send Message"}
      </button>
    </form>
  );
};

export default ContactForm;
